import { safeNumber, parseMonthToValue } from './dataUtils'

export interface QuestionInfo {
    name: string
    code: string
    fullText: string
}

export interface MonthInfo {
    label: string
    value: number
    date: Date
}

export interface HuddleTotals {
    [key: string]: { num: number; denom: number }
}

export const HUDDLE_COLORS = {
    'Yes Huddle': '#3F548D',
    'No Huddle': '#D79852'
} as const

export const normalizeHuddleGroup = (v: any): string => {
    const s = String(v || "").trim().toLowerCase()
    if (!s) return ""
    if (s === "yes huddle" || s === "yes") return "Yes Huddle"
    if (s === "no huddle" || s === "no") return "No Huddle"
    if (s === "notsure" || s === "not sure" || s === "not_sure") return "Not sure Huddle"
    if (s.includes("not") && s.includes("sure")) return "Not sure Huddle"
    return String(v || "").trim()
}

export const extractRspNumber = (questionName: string): number | null => {
    const s = String(questionName || "").trim()
    const m = s.match(/rsp[_-]?(\d+)/i)
    if (!m) return null
    const n = parseInt(m[1], 10)
    return Number.isFinite(n) ? n : null
}

export const extractChoiceNumber = (questionName: string): number | null => {
    const s = String(questionName || "").trim()
    const m = s.match(/choic(?:e|es)[\s_-]?(\d+)/i)
    if (!m) return null
    const n = parseInt(m[1], 10)
    return Number.isFinite(n) ? n : null
}

export const codeForQuestionName = (questionName: string): string => {
    const rspNum = extractRspNumber(questionName)
    if (rspNum != null) return `RSP${rspNum}`
    const choiceNum = extractChoiceNumber(questionName)
    if (choiceNum != null) return `Choice ${choiceNum}`
    return questionName
}

const HIDDEN_AGREEMENT_CHOICES = [3, 4]
const HIDDEN_DISAGREEMENT_CHOICES = [13, 14]

const filterHiddenChoices = <T extends { name: string }>(questions: T[], hiddenChoices: number[]): T[] => {
    return questions.filter(q => {
        const choiceNum = extractChoiceNumber(q.name)
        return choiceNum == null || !hiddenChoices.includes(choiceNum)
    })
}

export const filterAgreementQuestions = <T extends { name: string }>(questions: T[]): T[] => {
    return filterHiddenChoices(questions, HIDDEN_AGREEMENT_CHOICES)
}

export const filterDisagreementQuestions = <T extends { name: string }>(questions: T[]): T[] => {
    return filterHiddenChoices(questions, HIDDEN_DISAGREEMENT_CHOICES)
}

export const sortQuestions = <T extends { name: string }>(questions: T[]): T[] => {
    return [...questions].sort((a, b) => {
        const aRsp = extractRspNumber(a.name)
        const bRsp = extractRspNumber(b.name)
        const aChoice = extractChoiceNumber(a.name)
        const bChoice = extractChoiceNumber(b.name)

        if (aRsp != null && bRsp != null) return aRsp - bRsp
        if (aRsp != null && bRsp == null) return -1
        if (aRsp == null && bRsp != null) return 1

        if (aChoice != null && bChoice != null) return aChoice - bChoice
        if (aChoice != null && bChoice == null) return -1
        if (aChoice == null && bChoice != null) return 1

        return a.name.localeCompare(b.name)
    })
}

export const extractQuestionsFromRows = (
    rows: any[],
    questionNameIdx: number,
    questionLabelIdx: number,
    labelFallbackMap?: Map<string, string>
): QuestionInfo[] => {
    const questionMap = new Map<string, QuestionInfo>()

    rows.forEach((row: any[]) => {
        const name = questionNameIdx >= 0 && row[questionNameIdx]
            ? String(row[questionNameIdx]).trim()
            : ""
        if (!name) return

        if (!questionMap.has(name)) {
            const code = codeForQuestionName(name)
            let fullText = questionLabelIdx >= 0 && row[questionLabelIdx]
                ? String(row[questionLabelIdx]).trim()
                : ""

            if ((!fullText || fullText === name) && labelFallbackMap?.has(name)) {
                fullText = labelFallbackMap.get(name)!
            }

            if (!fullText) fullText = name

            questionMap.set(name, { name, code, fullText })
        }
    })

    return sortQuestions(Array.from(questionMap.values()))
}

export const buildQuestionLabelMap = (
    rows: any[],
    questionNameIdx: number,
    questionLabelIdx: number
): Map<string, string> => {
    const labelMap = new Map<string, string>()
    
    if (questionNameIdx === -1 || questionLabelIdx === -1) return labelMap
    
    rows.forEach((row: any[]) => {
        if (!row) return
        const name = String(row[questionNameIdx] || "").trim()
        const label = String(row[questionLabelIdx] || "").trim()
        if (name && label && label !== name && !labelMap.has(name)) {
            labelMap.set(name, label)
        }
    })
    
    return labelMap
}

const buildMonthListFromValues = (monthValues: Set<number>): MonthInfo[] => {
    if (monthValues.size === 0) return []

    const sortedValues = Array.from(monthValues).sort((a, b) => a - b)
    const minVal = sortedValues[0]
    const maxVal = sortedValues[sortedValues.length - 1]

    const minYear = Math.floor(minVal / 100)
    const minMonth = minVal % 100
    const maxYear = Math.floor(maxVal / 100)
    const maxMonth = maxVal % 100

    const diffYears = maxYear - minYear
    const diffMonths = maxMonth - minMonth
    const totalMonths = Math.max(1, (diffYears * 12) + diffMonths + 1)

    const list: MonthInfo[] = []
    const date = new Date(maxYear, maxMonth - 1, 1)

    for (let i = 0; i < totalMonths; i++) {
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        const label = date.toLocaleString('en-US', {
            month: 'short',
            year: 'numeric'
        })
        const value = year * 100 + month

        list.unshift({
            label,
            value,
            date: new Date(date)
        })

        date.setMonth(date.getMonth() - 1)
    }

    return list
}

export const extractActualMonthValues = (rows: any[], monthIdx: number): Set<number> => {
    const monthValues = new Set<number>()
    if (rows.length === 0 || monthIdx === -1) return monthValues
    rows.forEach(row => {
        if (row && monthIdx < row.length) {
            const val = parseMonthToValue(row[monthIdx])
            if (val > 0) monthValues.add(val)
        }
    })
    return monthValues
}

export const extractActualMonthValuesFromObjects = <T>(
    data: T[],
    getDateValue: (item: T) => any
): Set<number> => {
    const monthValues = new Set<number>()
    if (!data || data.length === 0) return monthValues
    data.forEach(item => {
        if (!item) return
        const raw = getDateValue(item)
        const val = parseMonthToValue(raw)
        if (val > 0) monthValues.add(val)
    })
    return monthValues
}

export const checkHasDataForRange = (
    sliderStart: number,
    sliderEnd: number,
    actualMonths: Set<number>
): boolean => {
    if (!sliderStart || !sliderEnd) return true
    if (!actualMonths.size) return false
    for (const m of actualMonths) {
        if (m >= sliderStart && m <= sliderEnd) return true
    }
    return false
}

export const generateMonthList = (rows: any[], monthIdx: number): MonthInfo[] => {
    const monthValues = extractActualMonthValues(rows, monthIdx)
    return buildMonthListFromValues(monthValues)
}

export const generateMonthListFromObjects = <T>(
    data: T[],
    getDateValue: (item: T) => any
): MonthInfo[] => {
    const monthValues = extractActualMonthValuesFromObjects(data, getDateValue)
    return buildMonthListFromValues(monthValues)
}

export const aggregateByHuddleGroup = (
    rows: any[],
    huddleIdx: number,
    numeratorIdx: number,
    denominatorIdx: number,
    excludeNotSure: boolean = true
): HuddleTotals => {
    const totals: HuddleTotals = {
        'Yes Huddle': { num: 0, denom: 0 },
        'No Huddle': { num: 0, denom: 0 }
    }

    rows.forEach((r: any[]) => {
        const g = normalizeHuddleGroup(r[huddleIdx])
        if (!totals[g]) return
        if (excludeNotSure && g === 'Not sure Huddle') return

        const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
        const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null

        if (num !== null) totals[g].num += num
        if (denom !== null) totals[g].denom += denom
    })

    return totals
}

export const calculatePercentage = (numerator: number, denominator: number): number | null => {
    if (!denominator || denominator === 0) return null
    return (numerator / denominator) * 100
}

export const TMB_AGREEMENT_SLIDER_KEY = 'tmb_agreement_slider'
export const TMB_SLIDER_KEY = 'tmb_slider'

export const getSliderIndicesFromValues = (
    monthList: MonthInfo[],
    sliderRange: [number, number],
    defaultMonths: number = 12
): [number, number] => {
    if (!monthList.length) return [-1, -1]
    const maxIndex = monthList.length - 1
    const defaultStartIdx = Math.max(0, maxIndex - defaultMonths + 1)

    const [startVal, endVal] = sliderRange
    if (!startVal || !endVal) return [defaultStartIdx, maxIndex]

    const pageMin = monthList[0].value
    const pageMax = monthList[maxIndex].value

    if (endVal < pageMin || startVal > pageMax) return [-1, -1]
    if (startVal < pageMin) return [-1, -1]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = monthList.findIndex(m => m.value === endVal)
    if (endIdx === -1) {
        for (let i = maxIndex; i >= 0; i--) {
            if (monthList[i].value <= endVal) { endIdx = i; break }
        }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))

    if (startIdx > endIdx) return [-1, -1]
    return [startIdx, endIdx]
}

export const initializeSliderValues = (
    monthList: MonthInfo[],
    currentValues: [number, number],
    defaultMonths: number = 12
): [number, number] => {
    if (monthList.length === 0) return [0, 0]

    const [startVal, endVal] = currentValues

    if (!startVal || !endVal || startVal < 10000) {
        const [s, e] = getDefaultSliderRange(monthList, defaultMonths)
        return [monthList[s].value, monthList[e].value]
    }

    return [startVal, endVal]
}

export const getDefaultSliderRange = (
    monthList: MonthInfo[],
    defaultMonths: number = 12
): [number, number] => {
    if (monthList.length === 0) return [0, 0]
    const maxIndex = monthList.length - 1

    const apr2025Index = monthList.findIndex(m => m.label === 'Apr 2025')
    if (apr2025Index !== -1) {
        return [apr2025Index, maxIndex]
    }

    const defaultStartIndex = Math.max(0, maxIndex - defaultMonths + 1)
    return [defaultStartIndex, maxIndex]
}

export const initializeSliderRange = (
    monthList: MonthInfo[],
    currentRange: [number, number],
    defaultMonths: number = 12
): [number, number] => {
    if (monthList.length === 0) return [0, 0]
    
    const maxIndex = monthList.length - 1
    
    if (currentRange[0] === 0 && currentRange[1] === 0) {
        return getDefaultSliderRange(monthList, defaultMonths)
    }
    
    const clampedStart = Math.min(currentRange[0], maxIndex)
    const clampedEnd = Math.min(currentRange[1], maxIndex)
    
    return [clampedStart, clampedEnd]
}
